import { yupResolver } from "@hookform/resolvers/yup";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Divider, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import { DialogModal } from "Components/_Shared/DialogModal";
import { Empty } from "Components/_Shared/Empty";
import { Loader } from "Components/_Shared/Loader";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlRadioButtons } from "Forms/Components/BlRadioButtons";
import { DatePicker } from "Forms/Components/DatePicker";
import { FormCharacterCounter } from "Forms/Components/FormCharacterCounter";
import { StyledLabelAdornment } from "Forms/Components/FormStyles";
import {
  getListContractProposalsQueryKey,
  useGetAnalysisSuitabilityStatement,
  useRejectAnalysisInvestmentCase,
  useSaveAnalysisSuitabilityStatement,
} from "Infrastructure/ApiZfp/AnalyzerApi";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import * as yup from "yup";
import { useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";

type JustificationFormType = {
  suitabilityInvestmentPlan?: string;
  suitabilityKnowledgeAndExperience?: string;
  suitabilityInvestmentHorizon?: string;
  suitabilityFinanceSituation?: string;
  suitabilityRiskTolerance?: string;
  suitabilityESG?: string;
  suitabilityAdditionalStatement?: string;
  isClientAgreed: string;
  agentStatement?: string;
  statementDate: Date | null;
};

const getEndOfToday = () => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const get14DaysAgo = () => {
  const d = new Date();
  d.setDate(d.getDate() - 14);
  d.setHours(0, 0, 0, 0);
  return d;
};

const isDisagreed = (isClientAgreed: unknown) => (isClientAgreed as string[])[0] === "false";

const MIN_TEXT_LENGTH = 10;
const MAX_TEXT_LENGTH = 300;

const withMinLength = (schema: yup.StringSchema, t: (key: any, options?: any) => string) =>
  schema
    .test(
      "min-length",
      t(Resources.Validation.String.Min, { count: MIN_TEXT_LENGTH }),
      (value) => !value || value.trim().length >= MIN_TEXT_LENGTH,
    )
    .max(MAX_TEXT_LENGTH, t(Resources.Validation.String.Max, { count: MAX_TEXT_LENGTH }));

const getSchema = (t: (key: any, options?: any) => string) =>
  yup.object({
    suitabilityInvestmentPlan: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityKnowledgeAndExperience: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityInvestmentHorizon: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityFinanceSituation: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityRiskTolerance: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityESG: withMinLength(yup.string(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.optional() : schema.required(t(Resources.Validation.Required)),
    ),
    suitabilityAdditionalStatement: withMinLength(yup.string(), t).optional(),
    isClientAgreed: yup.string().required(t(Resources.Validation.Required)),
    agentStatement: withMinLength(yup.string().trim(), t).when("isClientAgreed", (v, schema) =>
      isDisagreed(v) ? schema.required(t(Resources.Validation.Required)) : schema.optional(),
    ),
    statementDate: yup
      .date()
      .typeError(t(Resources.Validation.BadValue))
      .required(t(Resources.Validation.Required))
      .max(getEndOfToday(), t(Resources.Validation.DateMustNotBeFuture))
      .min(get14DaysAgo(), t(Resources.Validation.DateMustNotBeOlderThan14Days))
      .nullable(),
  });

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export const GoalFulfillmentJustification = ({ isOpen, onClose }: Props) => {
  const { t } = useResource();
  const r = Resources.AnalysisDetail.GoalsFulfillment.Nsure.Justification;
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { fetchGrowUpContractingLink } = useGoalsFulfillmentContext();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useGetAnalysisSuitabilityStatement(analysisId, versionId);
  const { mutate: save, isPending: isSaving } = useSaveAnalysisSuitabilityStatement();
  const { mutate: rejectCase, isPending: isRejecting } = useRejectAnalysisInvestmentCase();

  const methods = useForm<JustificationFormType>({
    resolver: yupResolver(getSchema(t)),
    mode: "onChange",
    defaultValues: {
      isClientAgreed: "true",
      statementDate: new Date(),
    },
  });

  const {
    control,
    watch,
    reset,
    clearErrors,
    formState: { errors },
    handleSubmit,
  } = methods;

  useEffect(() => {
    const s = data?.suitabilityStatement;
    if (!s) return;
    reset({
      suitabilityInvestmentPlan: s.suitabilityInvestmentPlan ?? "",
      suitabilityKnowledgeAndExperience: s.suitabilityKnowledgeAndExperience ?? "",
      suitabilityInvestmentHorizon: s.suitabilityInvestmentHorizon ?? "",
      suitabilityFinanceSituation: s.suitabilityFinanceSituation ?? "",
      suitabilityRiskTolerance: s.suitabilityRiskTolerance ?? "",
      suitabilityESG: s.suitabilityESG ?? "",
      suitabilityAdditionalStatement: s.suitabilityAdditionalStatement ?? "",
      isClientAgreed: s.isClientAgreed === false ? "false" : "true",
      agentStatement: s.agentStatement ?? "",
      statementDate: s.statementDate ? new Date(s.statementDate) : null,
    });
  }, [data, reset]);

  const isClientDisagreed = watch("isClientAgreed") === "false";

  useEffect(() => {
    if (isClientDisagreed) {
      clearErrors([
        "suitabilityInvestmentPlan",
        "suitabilityKnowledgeAndExperience",
        "suitabilityInvestmentHorizon",
        "suitabilityFinanceSituation",
        "suitabilityRiskTolerance",
        "suitabilityESG",
      ]);
    }
  }, [isClientDisagreed, clearErrors]);

  const onSave = (formData: JustificationFormType) => {
    const onError = () => toast.error(t(Resources.AnalysisDetail.Toast.ActionError));

    if (isClientDisagreed) {
      rejectCase(
        {
          analysisId,
          versionId,
          data: { reason: formData.agentStatement ?? "" },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: getListContractProposalsQueryKey(analysisId, versionId) });
            toast.success(t(Resources.AnalysisDetail.Toast.InvestmentCaseRejected));
            onClose();
          },
          onError,
        },
      );
      return;
    }

    save(
      {
        analysisId,
        versionId,
        data: {
          suitabilityInvestmentPlan: formData.suitabilityInvestmentPlan,
          suitabilityKnowledgeAndExperience: formData.suitabilityKnowledgeAndExperience,
          suitabilityInvestmentHorizon: formData.suitabilityInvestmentHorizon,
          suitabilityFinanceSituation: formData.suitabilityFinanceSituation,
          suitabilityRiskTolerance: formData.suitabilityRiskTolerance,
          suitabilityESG: formData.suitabilityESG,
          suitabilityAdditionalStatement: formData.suitabilityAdditionalStatement ?? null,
          suitabilityDate: formData.statementDate ? formData.statementDate.toISOString() : null,
          isClientAgreed: formData.isClientAgreed === "true",
          agentStatement: formData.agentStatement ?? null,
          statementDate: formData.statementDate ? formData.statementDate.toISOString() : null,
        },
      },
      {
        onSuccess: async () => {
          try {
            const growUpContractingLink = await fetchGrowUpContractingLink();
            if (growUpContractingLink?.url) {
              window.location.href = growUpContractingLink.url;
            } else {
              toast.error(t(Resources.AnalysisDetail.Toast.GrowUpRedirectError));
              onClose();
            }
          } catch {
            toast.error(t(Resources.AnalysisDetail.Toast.GrowUpRedirectError));
            onClose();
          }
        },
        onError,
      },
    );
  };

  const isBusy = isSaving || isRejecting;
  const saveButton = (
    <Button onClick={handleSubmit(onSave)} loading={isBusy} disabled={isBusy}>
      {isClientDisagreed ? t(r.EndNegotiation) : t(r.ContinueToGrowUp)}
    </Button>
  );

  const content = isLoading ? (
    <Loader />
  ) : isError ? (
    <Empty
      title={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Title)}
      text={t(Resources.AnalysisDetail.GoalsFulfillment.Error.Text)}
      icon={<ErrorOutlineIcon sx={{ fontSize: "40px", color: "grey.500" }} />}
    />
  ) : (
    <Stack spacing={Spacing.XL}>
      <FormProvider {...methods}>
        <form>
          <Stack spacing={Spacing.XXL}>
            <BlFormInput
              control={control}
              name="suitabilityInvestmentPlan"
              label={t(r.SuitabilityInvestmentPlan)}
              placeholder={t(r.SuitabilityInvestmentPlanPlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityInvestmentPlan"
                />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityKnowledgeAndExperience"
              label={t(r.SuitabilityKnowledgeAndExperience)}
              placeholder={t(r.SuitabilityKnowledgeAndExperiencePlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityKnowledgeAndExperience"
                />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityInvestmentHorizon"
              label={t(r.SuitabilityInvestmentHorizon)}
              placeholder={t(r.SuitabilityInvestmentHorizonPlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityInvestmentHorizon"
                />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityFinanceSituation"
              label={t(r.SuitabilityFinanceSituation)}
              placeholder={t(r.SuitabilityFinanceSituationPlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityFinanceSituation"
                />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityRiskTolerance"
              label={t(r.SuitabilityRiskTolerance)}
              placeholder={t(r.SuitabilityRiskTolerancePlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityRiskTolerance"
                />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityESG"
              label={t(r.SuitabilityESG)}
              placeholder={t(r.SuitabilityESGPlaceholder)}
              errors={errors}
              labelEndAdornment={isClientDisagreed ? undefined : <StyledLabelAdornment />}
              characterCounter={
                <FormCharacterCounter characterLimit={MAX_TEXT_LENGTH} characterMinLength={MIN_TEXT_LENGTH} name="suitabilityESG" />
              }
            />
            <BlFormInput
              control={control}
              name="suitabilityAdditionalStatement"
              label={t(r.SuitabilityAdditionalStatement)}
              placeholder={t(r.SuitabilityAdditionalStatementPlaceholder)}
              errors={errors}
              characterCounter={
                <FormCharacterCounter
                  characterLimit={MAX_TEXT_LENGTH}
                  characterMinLength={MIN_TEXT_LENGTH}
                  name="suitabilityAdditionalStatement"
                />
              }
            />
            <Divider />
            <Typography variant="h3">{"Vyjádření klienta"}</Typography>
            <BlRadioButtons
              control={control}
              name="isClientAgreed"
              label={t(r.IsClientAgreed)}
              codeList={[
                { code: "true", name: t(r.IsClientAgreedOptionA) },
                { code: "false", name: t(r.IsClientAgreedOptionB) },
              ]}
              errors={errors}
              row={false}
              gridSize={12}
              slotProps={{ typography: { sx: { "& .MuiTypography-body2": { fontWeight: "normal" } } } }}
            />
            {isClientDisagreed && (
              <BlFormInput
                control={control}
                name="agentStatement"
                label={t(r.AgentStatement)}
                placeholder={t(r.AgentStatementPlaceholder)}
                errors={errors}
                multiline
                rows={3}
                labelEndAdornment={<StyledLabelAdornment />}
                characterCounter={
                  <FormCharacterCounter characterLimit={MAX_TEXT_LENGTH} characterMinLength={MIN_TEXT_LENGTH} name="agentStatement" />
                }
              />
            )}
            <DatePicker
              control={control}
              name="statementDate"
              label={t(r.StatementDate)}
              errors={errors}
              disableFuture
              labelEndAdornment={<StyledLabelAdornment />}
            />
          </Stack>
        </form>
      </FormProvider>
    </Stack>
  );

  return (
    <DialogModal
      isOpen={isOpen}
      onClose={onClose}
      title={t(r.Title)}
      subtitle={<Alert variant="info" text={t(r.TitleDescription)} />}
      content={content}
      primaryAction={saveButton}
      size="lg"
    />
  );
};
